'use client';

import { Box, Portal, Skeleton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { radius } from '@odys/design-system/tokens/radius';
import { useTranslations } from 'next-intl';

import { IMPERSONATION_BANNER_HEIGHT_VAR } from '@/components/auth/impersonation-banner-height';
import type { ZoneGroup } from '@/components/homepage/groupByZone';

import { DestinationMenuCard } from './DestinationMenuCard';
import { DestinationsZoneTabs } from './DestinationsZoneTabs';

type Props = {
    open: boolean;
    groups: ZoneGroup[];
    loading: boolean;
    isError?: boolean;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onNavigate: () => void;
};

/**
 * Sous le pill navbar : 16 (son `top`) + ~56 (sa hauteur) + 8 de gap, plus la hauteur de la bannière
 * d'impersonation fixée en haut de page. Elle décale la navbar (cf. `Navbar.tsx`) : sans l'ajouter
 * ici aussi, le panneau resterait à 80px alors que la navbar est descendue, et il ouvrirait derrière
 * elle — son `zIndex` est `appBar - 1`. La variable n'existe que quand la bannière s'affiche, d'où
 * le fallback à 0px.
 */
const PANEL_TOP = `calc(80px + var(${IMPERSONATION_BANNER_HEIGHT_VAR}, 0px))`;

export function DestinationsMegaMenu({
    open,
    groups,
    loading,
    isError,
    onMouseEnter,
    onMouseLeave,
    onNavigate,
}: Props) {
    const t = useTranslations('nav');

    if (!open) return null;

    function renderContent() {
        if (loading) return <SkeletonGrid />;
        if (isError) {
            return (
                <Box
                    sx={(theme) => ({
                        textAlign: 'center',
                        py: 4,
                        color: alpha(theme.palette.common.white, 0.7),
                        fontSize: 14,
                    })}
                >
                    {t('destinationsError')}
                </Box>
            );
        }
        if (groups.length === 0) {
            return (
                <Box
                    sx={(theme) => ({
                        textAlign: 'center',
                        py: 4,
                        color: alpha(theme.palette.common.white, 0.5),
                        fontSize: 14,
                    })}
                >
                    {t('destinationsEmpty')}
                </Box>
            );
        }
        return (
            <DestinationsZoneTabs groups={groups} dark>
                {(tiles) => (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                            gap: 1.5,
                        }}
                    >
                        {tiles.map((tile) => (
                            <DestinationMenuCard
                                key={tile.id}
                                tile={tile}
                                onNavigate={onNavigate}
                                triggerContext="destinations_mega_menu"
                                dark
                            />
                        ))}
                    </Box>
                )}
            </DestinationsZoneTabs>
        );
    }

    return (
        <Portal>
            {/*
             * Approche single-element : position:fixed centré via left:50% +
             * transform:translateX(-50%). Le backdropFilter est sur l'élément
             * lui-même → il voit le contenu de la page derrière (le hero).
             *
             * Un transform sur l'élément LUI-MÊME ne bloque pas son propre
             * backdrop-filter (le filtre s'applique sur le backdrop avant
             * l'opacity/transform). Le problème précédent était un transform
             * sur un ANCÊTRE qui créait un groupe compositing isolé.
             */}
            <Box
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                sx={(theme) => ({
                    position: 'fixed',
                    top: PANEL_TOP,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 'calc(100vw - 32px)',
                    maxWidth: 1200,
                    zIndex: theme.zIndex.appBar - 1,
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    backgroundColor: alpha(theme.palette.common.black, 0.3),
                    borderRadius: radius.lg,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.common.white, 0.08),
                    boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.24)}`,
                    p: 2,
                    maxHeight: 320,
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    animation: 'destinations-panel-in 180ms ease-out both',
                    '@keyframes destinations-panel-in': {
                        from: { opacity: 0 },
                        to: { opacity: 1 },
                    },
                })}
            >
                {renderContent()}
            </Box>
        </Portal>
    );
}

function SkeletonGrid() {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: 1.5,
                pt: 1,
            }}
        >
            {Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={100} />
            ))}
        </Box>
    );
}
